/**
 * Mount de la galería `/galeria.html` — Fase 10d.
 *
 * Página pública (allowAnonymous): visitantes sin sesión la ven, pero
 * el botón SUBIR FOTOS y el botón BORRAR del lightbox solo aparecen
 * para usuarios autenticados (con sus permisos).
 */

import { hasRole, type GalleryPhoto, type Role } from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { openConfirmModal } from '../confirm-modal/index.js';
import {
  deleteGalleryPhoto,
  fetchGallery,
  uploadGalleryPhotos,
} from './api.js';
import { openLightbox } from './lightbox.js';
import {
  renderError,
  renderGaleriaActions,
  renderGalleryShell,
  renderGrid,
} from './markup.js';
import { openUploadModal } from './upload-modal.js';

import './galeria.css';

interface PageState {
  contentRoot: HTMLElement;
  currentUserId: string | null;
  currentRole: Role | null;
  photos: GalleryPhoto[];
}

export async function mountGaleria(): Promise<void> {
  const { contentRoot, user } = await mountChrome({
    activeSection: 'galeria',
    allowAnonymous: true,
  });

  const canUpload = user !== null;
  contentRoot.innerHTML = renderGalleryShell();
  replaceActionsSidebar(renderGaleriaActions(canUpload));

  const result = await fetchGallery();
  if (!result.ok) {
    showError(contentRoot, result.message);
    return;
  }

  const state: PageState = {
    contentRoot,
    currentUserId: user?.id ?? null,
    currentRole: (user?.role as Role | undefined) ?? null,
    photos: result.data.photos,
  };

  activeState = state;
  renderState(state);
  wireGridClicks(state);
  if (canUpload) wireUploadButton();
}

/** Sustituye el bloque ACCIONES por defecto del chrome derecho. */
function replaceActionsSidebar(html: string): void {
  const host = document.querySelector<HTMLElement>(
    '.chrome-sidebar--right .chrome-actions',
  );
  if (host) host.outerHTML = html;
}

// ─── Render ──────────────────────────────────────────────────────────

function renderState(state: PageState): void {
  const grid = state.contentRoot.querySelector<HTMLElement>(
    '[data-zone="grid"]',
  );
  const empty = state.contentRoot.querySelector<HTMLElement>(
    '[data-zone="empty"]',
  );
  if (!grid || !empty) return;
  grid.innerHTML = renderGrid(state.photos);
  empty.hidden = state.photos.length > 0;
}

function showError(root: HTMLElement, message: string): void {
  const stage = root.querySelector<HTMLElement>('.gal-stage');
  if (stage) stage.insertAdjacentHTML('beforeend', renderError(message));
}

// ─── Click thumbnail → lightbox ──────────────────────────────────────

function wireGridClicks(state: PageState): void {
  const grid = state.contentRoot.querySelector<HTMLElement>(
    '[data-zone="grid"]',
  );
  if (!grid) return;
  grid.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const thumb = target.closest<HTMLElement>('.gal-thumb');
    if (!thumb) return;
    const id = thumb.dataset['photoId'];
    if (!id) return;
    const startIndex = state.photos.findIndex((p) => p.id === id);
    if (startIndex < 0) return;
    void openLightbox({
      photos: state.photos,
      startIndex,
      canDelete: (p) => canDelete(state, p),
      onDelete: async (photoId) => {
        const ok = await openConfirmModal({
          title: 'Borrar foto',
          message: '¿Seguro que quieres borrar esta foto? No se puede deshacer.',
          confirmLabel: 'Borrar',
          danger: true,
        });
        if (!ok) return false;
        const res = await deleteGalleryPhoto(photoId);
        if (!res.ok) {
          showError(state.contentRoot, `No se pudo borrar: ${res.message}`);
          return false;
        }
        state.photos = state.photos.filter((p) => p.id !== photoId);
        renderState(state);
        return true;
      },
    });
  });
}

function canDelete(state: PageState, p: GalleryPhoto): boolean {
  if (!state.currentUserId) return false;
  if (p.uploadedBy === state.currentUserId) return true;
  if (state.currentRole && hasRole(state.currentRole, 'admin')) return true;
  return false;
}

// ─── Botón SUBIR FOTOS ───────────────────────────────────────────────

function wireUploadButton(): void {
  // El botón vive en el sidebar derecho del chrome (no en contentRoot).
  // Lo buscamos en document.
  const btn = document.querySelector<HTMLButtonElement>(
    '[data-gal-action="upload"]',
  );
  btn?.addEventListener('click', () => {
    // El state se cierra por closure dentro de mountGaleria → handler
    // capturado por wireUploadButton(state) ya no funciona; pasamos
    // state explícito.
    void handleUploadFromState();
  });
}

// Necesitamos referencia al state desde wireUploadButton; usamos un
// puntero a nivel de módulo. Es feo pero la página solo monta una
// vez por sesión.
let activeState: PageState | null = null;

async function handleUploadFromState(): Promise<void> {
  if (!activeState) return;
  await handleUpload(activeState);
}

async function handleUpload(state: PageState): Promise<void> {
  const result = await openUploadModal();
  if (!result) return;
  const res = await uploadGalleryPhotos(result.files, result.caption);
  if (!res.ok) {
    showError(state.contentRoot, `No se pudo subir: ${res.message}`);
    return;
  }
  // Las nuevas fotos al principio (orden createdAt desc).
  state.photos = [...res.photos, ...state.photos];
  renderState(state);
}
